import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, MailCheck, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { portalApi, setToken } from "@/lib/portalApi";
import { usePortalAuth } from "@/context/PortalAuthContext";

const shell =
  "min-h-screen bg-[#020617] flex flex-col";
const field =
  "w-full bg-[#0B1220] border border-slate-800 px-4 py-3 text-white text-sm placeholder:text-slate-600 focus:border-[#D4AF37] focus:outline-none transition-colors";
const label = "block text-xs uppercase tracking-widest text-slate-500 mb-2";
const primaryBtn =
  "w-full bg-[#D4AF37] text-slate-950 py-3.5 text-xs uppercase tracking-[0.2em] font-medium hover:bg-[#B5952F] disabled:opacity-60 transition-colors flex items-center justify-center gap-2";

const Shell = ({ eyebrow, title, subtitle, children }) => (
  <div className={shell}>
    <div
      className="pointer-events-none fixed inset-0"
      style={{
        background:
          "radial-gradient(45rem 30rem at 80% 0%, rgba(212,175,55,0.12), transparent 60%), radial-gradient(40rem 28rem at 0% 100%, rgba(56,89,163,0.16), transparent 65%)",
      }}
    />
    <div className="relative px-6 py-6">
      <Link
        to="/portal/login"
        className="inline-flex items-center gap-2 text-slate-500 hover:text-[#D4AF37] text-xs uppercase tracking-widest transition-colors"
        data-testid="reset-back-to-login"
      >
        <ArrowLeft size={14} /> Back to sign in
      </Link>
    </div>
    <div className="relative flex-1 flex items-center justify-center px-6 pb-16">
      <div className="w-full max-w-md" style={{ animation: "fadeInUp 0.5s ease-out both" }}>
        <p className="text-[#D4AF37] text-xs uppercase tracking-[0.3em] mb-4">{eyebrow}</p>
        <h1 className="font-serif text-3xl md:text-4xl text-white mb-3">{title}</h1>
        <p className="text-slate-400 text-sm mb-9 leading-relaxed">{subtitle}</p>
        {children}
      </div>
    </div>
  </div>
);

export const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await portalApi.forgotPassword(email);
      setSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <Shell
        eyebrow="Client Portal"
        title="Check your inbox"
        subtitle="If that email has an account with us, a reset link is on its way. The link works once and expires in one hour."
      >
        <div className="border border-slate-800 bg-[#0B1220] p-6 flex items-start gap-4" data-testid="forgot-sent">
          <MailCheck className="text-[#D4AF37] shrink-0 mt-0.5" size={18} />
          <p className="text-slate-400 text-sm leading-relaxed">
            Nothing after a few minutes? Check your spam folder, or{" "}
            <button onClick={() => setSent(false)} className="text-[#D4AF37] hover:underline" data-testid="forgot-try-again">
              try another address
            </button>
            .
          </p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell
      eyebrow="Client Portal"
      title="Forgot your password?"
      subtitle="Enter the email you signed up with and we'll send you a secure link to choose a new one."
    >
      <form onSubmit={submit} className="space-y-5" data-testid="forgot-password-form">
        <div>
          <label className={label} htmlFor="forgot-email">Email</label>
          <input
            id="forgot-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={field}
            placeholder="you@company.com"
            data-testid="forgot-email-input"
          />
        </div>
        {error && (
          <p className="text-red-400 text-sm border border-red-500/30 bg-red-500/10 px-4 py-3" data-testid="forgot-error">
            {error}
          </p>
        )}
        <button type="submit" disabled={loading} className={primaryBtn} data-testid="forgot-submit-btn">
          {loading && <Loader2 className="animate-spin" size={14} />}
          {loading ? "Sending" : "Send reset link"}
        </button>
      </form>
    </Shell>
  );
};

export const ResetPassword = () => {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const { setUser } = usePortalAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    if (password !== confirm) return setError("Those two passwords don't match.");
    setLoading(true);
    try {
      const data = await portalApi.resetPassword(token, password);
      setToken(data.token);
      setUser(data.user);
      navigate("/portal", { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <Shell
        eyebrow="Client Portal"
        title="This link is incomplete"
        subtitle="The reset link is missing its token. Request a fresh one and try again."
      >
        <Link
          to="/portal/forgot-password"
          className="inline-block border border-[#D4AF37] text-[#D4AF37] px-7 py-3.5 text-xs uppercase tracking-[0.2em] hover:bg-[#D4AF37] hover:text-slate-950 transition-colors"
          data-testid="reset-request-new-link"
        >
          Request a new link
        </Link>
      </Shell>
    );
  }

  return (
    <Shell eyebrow="Client Portal" title="Choose a new password" subtitle="Pick something at least 8 characters long.">
      <form onSubmit={submit} className="space-y-5" data-testid="reset-password-form">
        <div>
          <label className={label} htmlFor="reset-password">New password</label>
          <div className="relative">
            <input
              id="reset-password"
              type={show ? "text" : "password"}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={field}
              placeholder="At least 8 characters"
              data-testid="reset-password-input"
            />
            <button
              type="button"
              onClick={() => setShow((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
              data-testid="reset-toggle-password"
            >
              {show ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>
        <div>
          <label className={label} htmlFor="reset-confirm">Confirm password</label>
          <input
            id="reset-confirm"
            type={show ? "text" : "password"}
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className={field}
            placeholder="Type it once more"
            data-testid="reset-confirm-input"
          />
        </div>
        {error && (
          <p className="text-red-400 text-sm border border-red-500/30 bg-red-500/10 px-4 py-3" data-testid="reset-error">
            {error}
          </p>
        )}
        <button type="submit" disabled={loading} className={primaryBtn} data-testid="reset-submit-btn">
          {loading && <Loader2 className="animate-spin" size={14} />}
          {loading ? "Saving" : "Save new password"}
        </button>
      </form>
    </Shell>
  );
};
